/**
 * Seasonly — season switching
 *
 * The initial season is resolved before first paint by the inline script in
 * snippets/seasonly-head.liquid. This module handles clicks on the season tabs,
 * keeps every tab/tile in sync, persists the visitor's choice for the session,
 * and sprinkles the hero with floating particles.
 */
(function () {
  const SEASONS = ['spring', 'summer', 'fall', 'winter'];
  const STORAGE_KEY = 'seasonly:season';
  const root = document.documentElement;

  function seasonFromDate(date) {
    const m = date.getMonth();
    if (m === 11 || m <= 1) return 'winter';
    if (m <= 4) return 'spring';
    if (m <= 7) return 'summer';
    return 'fall';
  }

  function current() {
    const s = root.getAttribute('data-season');
    return SEASONS.includes(s) ? s : seasonFromDate(new Date());
  }

  function remember(season) {
    try {
      sessionStorage.setItem(STORAGE_KEY, season);
    } catch (e) {
      /* storage unavailable — fine */
    }
  }

  function syncControls(season) {
    document.querySelectorAll('[data-season-tab]').forEach((el) => {
      const active = el.getAttribute('data-season') === season;
      el.setAttribute('aria-selected', active ? 'true' : 'false');
      if (el.hasAttribute('data-season-tile')) el.setAttribute('aria-current', active ? 'true' : 'false');
    });
    document.querySelectorAll('[data-season-label]').forEach((el) => {
      el.textContent = season.charAt(0).toUpperCase() + season.slice(1);
    });
  }

  function set(season, { persist = true } = {}) {
    if (!SEASONS.includes(season)) return;
    root.setAttribute('data-season', season);
    root.setAttribute('data-season-source', 'user');
    if (persist) remember(season);
    syncControls(season);
    renderParticles(season);
    document.dispatchEvent(new CustomEvent('seasonly:change', { detail: { season } }));
  }

  /* ---------- Tabs ---------- */
  document.addEventListener('click', (event) => {
    const tab = event.target.closest('[data-season-tab]');
    if (!tab) return;
    const season = tab.getAttribute('data-season');
    if (!SEASONS.includes(season)) return;

    // On the home page the whole page re-themes in place; elsewhere we
    // re-theme first and then go to the season's collection.
    const inPlace = root.getAttribute('data-season-inplace') === 'true' && !tab.hasAttribute('data-season-navigate');
    if (inPlace) {
      event.preventDefault();
      set(season);
      const target = document.querySelector('[data-season-scroll-target]');
      if (target && tab.hasAttribute('data-season-scroll')) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      remember(season);
      root.setAttribute('data-season', season);
    }
  });

  /* ---------- Particles ---------- */
  const PARTICLE_SHAPES = {
    spring:
      '<svg viewBox="0 0 24 24"><g fill="#f2a5b8"><circle cx="12" cy="6" r="4"/><circle cx="17.5" cy="10" r="4"/><circle cx="15.5" cy="16.5" r="4"/><circle cx="8.5" cy="16.5" r="4"/><circle cx="6.5" cy="10" r="4"/></g><circle cx="12" cy="12" r="2.4" fill="#f7d774"/></svg>',
    summer:
      '<svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="6" fill="#ffd25a" opacity="0.9"/><circle cx="12" cy="12" r="10" fill="#ffd25a" opacity="0.25"/></svg>',
    fall: '<svg viewBox="0 0 24 24"><path d="M12 2c3 3 8 4 9 10-4 1-6 4-9 10-3-6-5-9-9-10 1-6 6-7 9-10z" fill="var(--leaf, #d47a3a)"/><path d="M12 4v16" stroke="rgba(0,0,0,.25)" stroke-width="1"/></svg>',
    winter:
      '<svg viewBox="0 0 24 24" fill="none" stroke="#ffffff" stroke-width="1.6" stroke-linecap="round"><path d="M12 2v20M2 12h20M5 5l14 14M19 5L5 19"/><path d="M12 2l-2 2M12 2l2 2M12 22l-2-2M12 22l2-2M2 12l2-2M2 12l2 2M22 12l-2-2M22 12l-2 2"/></svg>',
  };
  const LEAF_COLORS = ['#d47a3a', '#c4622d', '#e0a458', '#8b5a2b', '#b8452a'];

  function renderParticles(season) {
    const hosts = document.querySelectorAll('[data-season-particles]');
    if (!hosts.length) return;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    hosts.forEach((host) => {
      host.innerHTML = '';
      if (reduce) return;
      const count = season === 'winter' ? 26 : season === 'summer' ? 14 : 18;
      const frag = document.createDocumentFragment();
      for (let i = 0; i < count; i++) {
        const p = document.createElement('span');
        p.className = 's-particle';
        const size = season === 'winter' ? 6 + Math.random() * 10 : 10 + Math.random() * 16;
        p.style.setProperty('--size', size + 'px');
        p.style.setProperty('--dur', 9 + Math.random() * 12 + 's');
        p.style.setProperty('--delay', -Math.random() * 18 + 's');
        p.style.setProperty('--drift', (Math.random() * 160 - 80).toFixed(0) + 'px');
        p.style.setProperty('--spin', (Math.random() * 720 - 360).toFixed(0) + 'deg');
        p.style.setProperty('--alpha', (0.5 + Math.random() * 0.45).toFixed(2));
        p.style.left = (Math.random() * 100).toFixed(1) + '%';
        if (season === 'fall') p.style.setProperty('--leaf', LEAF_COLORS[i % LEAF_COLORS.length]);
        p.innerHTML = PARTICLE_SHAPES[season] || '';
        frag.appendChild(p);
      }
      host.appendChild(frag);
    });
  }

  /* ---------- Boot ---------- */
  function boot() {
    const season = current();
    syncControls(season);
    renderParticles(season);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Keep the theme editor in sync when sections re-render.
  document.addEventListener('shopify:section:load', () => syncControls(current()));

  window.Seasonly = { set, current, seasons: SEASONS, seasonFromDate };
})();
